import { useMemo } from 'react';

import { Skeleton, Typography, Stack } from '@cast/design-system';

import { useSimulatedClusterDemoUi } from '@cast/infrastructure-demo';
import { NewLabelChip } from 'components/chips';
import { CopyToClipboard } from 'components/clickable-actions/CopyToClipboard';
import {
  RoutedTab,
  RoutedTabContent,
  RoutedTabs,
} from 'components/tabs/routed';
import { useNodeStorageQuery } from 'hooks/queries/nodes';
import { useNodeStorageStateQuery } from 'hooks/queries/nodes/useNodeStorageStateQuery';
import { useParams } from 'hooks/useParams';
import { PageHeader } from 'main-layout/PageHeader';

import { NodeDetailsBreadcrumbs } from './_components/NodeDetailsBreadcrumbs';
import { OverviewTabContent } from './_components/OverviewTabContent';
import { StorageTabContent } from './_components/StorageTabContent';
import { WorkloadsTabContent } from './_components/WorkloadsTabContent';
import { useStorageOptimizationAbilities } from '../../storage-optimization/hooks/useStorageOptimizationAbilities';
import { NodeActionsMenu } from '../_components/NodeActionsMenu';
import { isTestNode, getMasterNodes } from '../_utils/common';
import { TestNodeChip } from '../components/TestNodeChip';
import { useNode } from '../hooks/useNode';
import { useNodes } from '../hooks/useNodes';
import { NodeDetailsTab } from '../types';
import { getNodeMetrics } from '../utils/getNodeMetrics';

import { useCluster } from '@cast/shared-cluster-context-data-access';

import { FailedToLoad } from '@cast/shared-messages-ui';

const WORKLOADS_REFETCH_INTERVAL_MS = 30_000;

export const NodeDetails = () => {
  const { nodeId } = useParams<{ nodeId: string }>();
  const { cluster, isLoading: isClusterLoading } = useCluster();
  const isSimulatedClusterDemoUi = useSimulatedClusterDemoUi();
  const {
    node,
    isLoading: isNodeLoading,
    isWorkloadsLoading,
    error: nodeError,
    refetch: nodeRefetch,
  } = useNode({
    nodeId,
    withIssues: true,
    withRebalancingWorkloads: true,
    withPricing: true,
    workloadsRefetchInterval: WORKLOADS_REFETCH_INTERVAL_MS,
  });
  const { nodes } = useNodes();
  const { canViewNodeLevelStop } = useStorageOptimizationAbilities();
  const masterNodesCount = useMemo(() => {
    const masterNodes = getMasterNodes(nodes || []);
    return masterNodes.length;
  }, [nodes]);

  const { data: storageData, isLoading: isStorageLoading } =
    useNodeStorageQuery({ clusterId: cluster.id });
  const { isLoading: isStorageStateLoading, storageState } =
    useNodeStorageStateQuery({
      clusterId: cluster.id,
      nodeName: node?.name,
    });
  const nodeMetrics = useMemo(
    () =>
      node
        ? getNodeMetrics(
            node,
            storageData?.nodes?.find((n) => n.nodeName === node.name)
          )
        : undefined,
    [node, storageData]
  );

  const isLoading =
    isClusterLoading ||
    isNodeLoading ||
    isStorageLoading ||
    isStorageStateLoading;

  if (nodeError) {
    return (
      <FailedToLoad title="Failed to load node details" refresh={nodeRefetch} />
    );
  }

  return (
    <Stack height="100%">
      <NodeDetailsBreadcrumbs
        currentNode={node}
        nodes={nodes}
        cluster={cluster}
      />
      <PageHeader
        heading={
          isNodeLoading ? (
            <Typography variant="h3">
              <Skeleton variant="text" width={300} />
            </Typography>
          ) : (
            <Stack direction="row" gap={8} flexGrow={1} alignItems="center">
              {!!node && isTestNode(node) && <TestNodeChip size="md" />}
              <Typography variant="h3">{node?.name}</Typography>
              <CopyToClipboard text={node?.name || ''} iconSize={20} />
            </Stack>
          )
        }
      >
        {node && !isSimulatedClusterDemoUi && (
          <NodeActionsMenu
            cluster={cluster}
            node={node}
            masterNodesCount={masterNodesCount}
          />
        )}
      </PageHeader>
      <RoutedTabs fullWidth sx={{ mb: 24 }} testId="node-details-tabs">
        <RoutedTab label="Overview" value={NodeDetailsTab.OVERVIEW} />
        <RoutedTab
          label={
            node?.workloads?.length
              ? `Workloads (${node.workloads.length})`
              : 'Workloads'
          }
          value={NodeDetailsTab.WORKLOADS}
        />
        {canViewNodeLevelStop && storageState?.storageStatsEnabled && (
          <RoutedTab
            label={
              <Stack direction="row" alignItems="center" gap={6}>
                Storage
                <NewLabelChip size="s" />
              </Stack>
            }
            value={NodeDetailsTab.STORAGE}
          />
        )}
        <RoutedTabContent value={NodeDetailsTab.OVERVIEW}>
          {!isLoading && node && nodeMetrics ? (
            <OverviewTabContent
              node={node}
              cluster={cluster}
              nodeMetrics={nodeMetrics}
            />
          ) : (
            <OverviewTabContent.Skeleton />
          )}
        </RoutedTabContent>
        <RoutedTabContent value={NodeDetailsTab.WORKLOADS}>
          <WorkloadsTabContent
            isLoading={isLoading || isWorkloadsLoading}
            workloads={node?.workloads}
            clusterId={cluster.id}
          />
        </RoutedTabContent>
        {canViewNodeLevelStop && storageState?.storageStatsEnabled && (
          <RoutedTabContent value={NodeDetailsTab.STORAGE}>
            <StorageTabContent
              node={node}
              cluster={cluster}
              isNodeLoading={isNodeLoading}
              storageState={storageState}
            />
          </RoutedTabContent>
        )}
      </RoutedTabs>
    </Stack>
  );
};
